import { components } from "@apiSchema";
import { iPermissionFields, iPermissionSection, PermissionLevelEnum, PermissionSection } from "./PermissionSection";
import { keyAttributesConfig, stockInfoAttributesConfig } from "./ChainBase";
import {
    capitalize,
    getFormattedThousand,
    getFormattedThousandWithDecimal,
    getRoundedToSign,
    sortByField
} from "@helpers";
import { ReactNode } from "react";
import { MENU_TYPE_DEFAULT_FILTER_TITLE } from "./ChainsFilters";
import { NO_VALUE_PLACEHOLDER } from "@models/index";

const DEFAULT_KEY_ATTRIBUTES_DATA: components["schemas"]["KeyAttributesDataViewModel"] = {
    airport: false,
    mall: false,
    lodging: false,
    collegeHospital: false,
    hasBreakfast: false,
    hasLunch: false,
    hasDinner: false,
    maxSquareFootage: 0,
    minSquareFootage: 0,
    franchisedPercent: 0,
    primaryDistributor: 'No data',
    pos: 'No data',
    priceLevel: -1,
    priceLevelString: '',
    deliverySystem: 'No data',
    averageRating: 0,
    alcohol: -1,
    foundedYear: 0,
    holdingCompany: 'No data',
    holdingCompanyWebsite: 'No data',
};

export enum ReportsNameEnum {
    Profile = "Profile",
    Firefly = "Chain Profile Report",
    LTOActivity = "Launches & Ratings LTO Activity Report",
    BrandFingerprints = "Brand Performance Analysis",
    Menu = "Menu Report",
    Gaps = "BYO Gap Analysis",
    None = ""
}

export function getChainReportName(reportName: string) {
    switch (reportName) {
        case 'Profile':
            return ReportsNameEnum.Profile;
        case 'Firefly Location Analysis':
            return ReportsNameEnum.Firefly;
        case 'LTO Activity Report':
            return ReportsNameEnum.LTOActivity;
        case 'Brand Fingerprints Analysis':
            return ReportsNameEnum.BrandFingerprints;
        case 'Menu Report':
            return ReportsNameEnum.Menu;
        case 'BYO Gap Analysis':
            return ReportsNameEnum.Gaps;
        default:
            return ReportsNameEnum.None;
    }
}

export interface iKeyAttribute {
    title: string;
    value: string | ReactNode;
    testId?: string;
}

export interface iGroupYearAttribute {
    grow: string;
    values: iKeyAttribute[];
}

export class GroupYearAttribute implements iGroupKeyAttribute {
    grow = '';
    values: iKeyAttribute[] = [];

    constructor(data?: iGroupYearAttribute) {
        if (data) {
            this.setData(data);
        }
    }

    private setData(data: iGroupKeyAttribute) {
        ({
            grow: this.grow,
            values: this.values,
        } = data);
    }
}

export enum SocialLinkEnum {
    facebook = "facebook",
    foursquare = "foursquare",
    instagram = "instagram",
    linkedin = "linkedin",
    pinterest = "pinterest",
    snapchat = "snapchat",
    tumblr = "tumblr",
    twitter = "twitter",
    wikipedia = "wikipedia",
    youtube = "youtube",
    zomato = "zomato",
    none = "none"
}


export interface iSocialLink {
    title: SocialLinkEnum;
    url: string;
    followersCount?: number | null;
}

export class KeyAttributesData {
    sales: iGroupYearAttribute = new GroupYearAttribute();
    auv: iGroupYearAttribute = new GroupYearAttribute();
    units: iGroupYearAttribute = new GroupYearAttribute();
    stocks: iKeyAttribute[] = [];
    keyAttributes: iKeyAttribute[] = [];
}

export interface iMainData {
    id: number;
    logoUrl: string;
    segmentName: string;
    segmentId: number;
    cuisinePrimary: string;
    unitsCount: number;
    name: string;
    blurb: string;
    company: string;
    images: string[];
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    fax: string;
    email: string;
    website: string;
    socialLinks: components["schemas"]["FFSocialLinks"];
    facebookFollowers: number | null;
    instagramFollowers: number | null;
    metroArea: string;
    stateFullName: string;
}

export interface iChainReport {
    name: ReportsNameEnum;
    price: number;
}

export interface iReportData extends iPermissionFields {
    data: iChainReport;
}

export interface iChainDetails {
    mainData: iMainData;
    keyAttributesData: iPermissionFields & {
        data: KeyAttributesData;
    };
    sectionsData: components["schemas"]["SectionDataViewModel"];
    reportsData: iReportData[];
    notes: iPermissionSection<any[]>;
}

interface iData {
    apiModel: components["schemas"]["ProfileViewModel"];
}

interface iGroupKeyAttribute {
    grow: string;
    values: iKeyAttribute[];
}

export class ChainDetails implements iChainDetails {
    mainData = ChainDetails.defaultData.mainData;
    keyAttributesData = ChainDetails.defaultData.keyAttributesData;
    sectionsData = ChainDetails.defaultData.sectionsData;
    reportsData = ChainDetails.defaultData.reportsData;
    notes = ChainDetails.defaultData.notes;

    static readonly defaultData: iChainDetails = {
        mainData: {
            id: 0,
            name: '',
            cuisinePrimary: '',
            segmentName: '',
            company: '',
            logoUrl: '',
            blurb: '',
            address: '',
            city: '',
            phone: '',
            email: '',
            fax: '',
            website: '',
            socialLinks: {
                wikipedia: '',
                facebook: '',
                twitter: '',
                instagram: '',
                youtube: '',
                pinterest: '',
                zomato: '',
                foursquare: '',
                snapchat: '',
                tumblr: '',
                linkedin: '',
            },
            facebookFollowers: null,
            instagramFollowers: null,
            images: [],
            metroArea: '',
            state: '',
            unitsCount: 0,
            zip: '',
            segmentId: 0,
            stateFullName: '',
        },
        keyAttributesData: {
            ...new PermissionSection(),
            data: new KeyAttributesData(),
        },
        sectionsData: {
            hasBrand: false,
            hasContacts: false,
            hasLocation: false,
            hasLto: false,
            hasMenu: false,
            hasSocials: false,
            isMtd: false,
        },
        reportsData: [],
        notes: {
            permissionLevel: PermissionLevelEnum.None,
            hasData: false,
        },
    };

    constructor(data?: iData) {
        if (data?.apiModel) {
            this.mapFromApi(data.apiModel);
        }
    }

    private setData(model: iChainDetails) {
        ({
            mainData: this.mainData,
            keyAttributesData: this.keyAttributesData,
            sectionsData: this.sectionsData,
            reportsData: this.reportsData,
            notes: this.notes,
        } = model);
    }

    static getGrowthString(value: number): string {
        if (!value && value !== 0) return NO_VALUE_PLACEHOLDER;
        return `${getRoundedToSign(value * 100)}%`;
    }

    static readonly getChainReport = (data?: components["schemas"]["ChainReportViewModel"]) => {
        if (!data) {
            return { name: ReportsNameEnum.None, price: 0 }
        }

        return {
            price: data.price ?? 0,
            name: getChainReportName(data.name ?? '')
        }
    }

    static mapUnitsItem(item: components["schemas"]["FFYearValueData"]): iKeyAttribute {
        return {
            title: `${item.year ?? ''}`,
            value: item.value ? getFormattedThousand(item.value) : NO_VALUE_PLACEHOLDER,
        }
    }

    static mapSalesItem(item: components["schemas"]["FFYearValueData"]): iKeyAttribute {
        return {
            title: `${item.year ?? ''}`,
            value: item.value ? `$${getFormattedThousandWithDecimal(item.value/(10**6), 1)}` : NO_VALUE_PLACEHOLDER,
        }
    }

    static mapAuvsItem(item: components["schemas"]["FFYearValueData"]): iKeyAttribute {
        return {
            title: `${item.year ?? ''}`,
            value: item.value ? `$${getFormattedThousand(item.value)}` : NO_VALUE_PLACEHOLDER,
        }
    }

    private mapFromApi(data: components["schemas"]["ProfileViewModel"]) {
        const { companyData, reportsData, notes } = data;

        const reports = (reportsData ?? [])
            .map(i => ({
                permissionLevel: PermissionSection.getPermissionLevel(i.permissionLevel),
                hasData: i.hasData ?? false,
                data: ChainDetails.getChainReport(i.data),
            }))
            .filter(report => report.data.name);

        const newData: iChainDetails = {
            mainData: {
                id: companyData?.id ?? ChainDetails.defaultData.mainData.id,
                logoUrl: companyData?.logoUrl ?? ChainDetails.defaultData.mainData.logoUrl,
                segmentName: companyData?.segmentName ?? ChainDetails.defaultData.mainData.segmentName,
                segmentId: companyData?.segmentId ?? ChainDetails.defaultData.mainData.segmentId,
                cuisinePrimary: capitalize(companyData?.cuisinePrimary ?? MENU_TYPE_DEFAULT_FILTER_TITLE),
                unitsCount: companyData?.unitsCount ?? ChainDetails.defaultData.mainData.unitsCount,
                name: companyData?.name ?? ChainDetails.defaultData.mainData.name,
                blurb: companyData?.blurb ?? ChainDetails.defaultData.mainData.blurb,
                company: companyData?.company ?? ChainDetails.defaultData.mainData.company,
                images: companyData?.images ?? ChainDetails.defaultData.mainData.images,
                address: companyData?.address ?? ChainDetails.defaultData.mainData.address,
                city: companyData?.city ?? ChainDetails.defaultData.mainData.city,
                state: companyData?.state ?? ChainDetails.defaultData.mainData.state,
                zip: companyData?.zip ?? ChainDetails.defaultData.mainData.zip,
                phone: companyData?.phone ?? ChainDetails.defaultData.mainData.phone,
                fax: companyData?.fax ?? ChainDetails.defaultData.mainData.fax,
                email: companyData?.email ?? ChainDetails.defaultData.mainData.email,
                website: companyData?.website ?? ChainDetails.defaultData.mainData.website,
                socialLinks: companyData?.socialLinks ?? ChainDetails.defaultData.mainData.socialLinks,
                facebookFollowers: companyData?.facebookFollowers ?? null,
                instagramFollowers: companyData?.instagramFollowers ?? null,
                metroArea: companyData?.metroArea ?? ChainDetails.defaultData.mainData.metroArea,
                stateFullName: companyData?.stateFullName ?? ChainDetails.defaultData.mainData.stateFullName,
            },
            keyAttributesData: ChainDetails.defaultData.keyAttributesData,
            sectionsData: data.sectionData ?? ChainDetails.defaultData.sectionsData,
            reportsData: reports,
            notes: {
                permissionLevel: PermissionSection.getPermissionLevel(notes?.permissionLevel),
                hasData: notes?.hasData ?? false,
            },
        };


        const units = companyData?.unitsByYear ?? [];
        const sales = companyData?.salesByYear ?? [];
        const auvs = companyData?.auvByYear ?? [];

        units.sort(sortByField("year", "desc"));
        sales.sort(sortByField("year", "desc"));
        auvs.sort(sortByField("year", "desc"));

        const keyAttributesData = data.keyAttributesData?.data ?? DEFAULT_KEY_ATTRIBUTES_DATA;
        const keyAttributesPermissionLevel = PermissionSection.getPermissionLevel(data.keyAttributesData?.permissionLevel);

        newData.keyAttributesData = {
            permissionLevel: keyAttributesPermissionLevel,
            hasData: data.keyAttributesData?.hasData ?? false,
            data: {
                keyAttributes: data.keyAttributesData?.hasData
                    ? keyAttributesConfig
                        .filter(i => i.getIsShown(keyAttributesData))
                        .map(i => ({
                            title: i.label,
                            value: i.getValue(keyAttributesData),
                            testId: i.getTestId ? i.getTestId(keyAttributesData) : undefined,
                        }))
                    : keyAttributesConfig.map(i => ({
                        title: i.label,
                        value: 'Content protected',
                    })),
                stocks: stockInfoAttributesConfig
                    .filter(i => i.getIsShown(companyData ?? {}))
                    .map(i => ({
                        title: i.label,
                        value: i.getValue(companyData ?? {}),
                    })),
                units: {
                    grow: ChainDetails.getGrowthString(companyData?.unitsGrowth ?? 0),
                    values: units.slice(0, 3).map(ChainDetails.mapUnitsItem)
                },
                sales: {
                    grow: ChainDetails.getGrowthString(companyData?.salesGrowth ?? 0),
                    values: sales.slice(0, 3).map(ChainDetails.mapSalesItem)
                },
                auv: {
                    grow: ChainDetails.getGrowthString(companyData?.auvGrowth ?? 0),
                    values: auvs.slice(0, 3).map(ChainDetails.mapAuvsItem)
                },
            },
        };

        this.setData(newData);
    }
}

export interface iProfileData {
    profileData: iPermissionFields & {
        data: ChainDetails;
    };
    isInTop1000: boolean;
}

export class ProfileData implements iProfileData {
    static readonly defaultData: iProfileData = {
        profileData: {
            ...new PermissionSection(),
            data: new ChainDetails(),
        },
        isInTop1000: true,
    };

    profileData = ProfileData.defaultData.profileData;
    isInTop1000 = ProfileData.defaultData.isInTop1000;
}